import { useCallback, useMemo, useState } from 'react'
import { SpeakingRole, toMs, Turn, TurnStatus, TurnType, WinType } from '@db8/shared'
import { Button } from '@/components/ui/button'
import { useHall } from '@/contexts/HallProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { useSocketContext } from '@/contexts/SocketProvider'
import { useMeeting } from '@videosdk.live/react-sdk'
import { useAxios } from '@/contexts/AxiosProvider'
import { useUser } from '@clerk/clerk-react'
import { useMeetingAppContext } from '@/MeetingAppContextDef'
import { sideBarModes } from '@/utils/common'
import { useFlowNotes } from '@/contexts/FlowNotesProvider'
import { useAzureTts } from '@/contexts/AzureTtsProvider'
import { NextControl } from './ItemControls'

type Props = {
  turn: Turn
}

export function CrossfireControls({ turn }: Props) {
  const { hall, hallMetadata, transcriptions, updateTranscript, setHallMetadata } = useHall()
  const { getUser, isBot } = useHallUsers()
  const socket = useSocketContext()
  const meeting: any = useMeeting()
  const axios = useAxios()
  const { user: clerkUser } = useUser()
  const { setSideBarMode } = useMeetingAppContext()
  const { fetchFlowNote } = useFlowNotes()
  const { audioRef } = useAzureTts()

  type Phase =
    | 'readyQuestion'
    | 'playingQuestion'
    | 'readyAnswer'
    | 'answerRecording'
    | 'playingAnswer'
    | 'readyAdvance'
  const [phase, setPhase] = useState<Phase>('readyQuestion')
  const [busy, setBusy] = useState(false)
  const [statusMsg, setStatusMsg] = useState('')

  const slot1 = useMemo(() => getUser(turn.primarySpeakerId), [turn.primarySpeakerId, getUser])
  const slot2 = useMemo(
    () => getUser(turn.secondarySpeakerId || ''),
    [turn.secondarySpeakerId, getUser],
  )

  const isCrossfire = useMemo(
    () => [TurnType.AffirmativeCrossNegative, TurnType.NegativeCrossAffirmative].includes(turn.type),
    [turn.type],
  )

  const canRun =
    !!hall?.id && !!clerkUser?.id && isCrossfire && !!turn.secondarySpeakerId && !!slot1 && !!slot2

  // Directed cross-ex: Slot 1 questions Slot 2
  const questioner = slot1
  const responder = slot2

  const questionerIsBot = !!questioner && isBot(questioner)
  const responderIsBot = !!responder && isBot(responder)

  const questionerIsHuman = !!questioner && !questionerIsBot
  const responderIsHuman = !!responder && !responderIsBot

  const human1 = questionerIsHuman ? questioner : null
  const human2 = responderIsHuman ? responder : null

  const emitStartTranscription = useCallback(
    (userIds: string[]) => {
      if (!hall?.id) return
      socket.emit('start-user-transcription', { hallId: hall.id, users: userIds.filter(Boolean) })
    },
    [socket, hall?.id],
  )

  const emitStopTranscription = useCallback(
    (userIds: string[]) => {
      if (!hall?.id) return
      socket.emit('stop-user-transcription', { hallId: hall.id, users: userIds.filter(Boolean) })
    },
    [socket, hall?.id],
  )

  const isValidParticipantId = useCallback(
    (videoSDKId?: string) => {
      if (!videoSDKId) return false
      // VideoSDK expects a real participantId; bots use placeholders like "bot-2" which will crash SDK calls.
      try {
        return Boolean(meeting?.participants?.has?.(videoSDKId) || meeting?.localParticipant?.id === videoSDKId)
      } catch {
        return false
      }
    },
    [meeting],
  )

  const muteParticipant = useCallback(
    (videoSDKId?: string) => {
      if (!isValidParticipantId(videoSDKId)) return
      try {
        // Match existing working behavior in ParticipantListItem:
        // - local participant: muteMic() (no args)
        // - remote participant: muteMic(participantId)
        const isLocal = meeting?.localParticipant?.id === videoSDKId
        if (typeof meeting?.muteMic === 'function') isLocal ? meeting.muteMic() : meeting.muteMic(videoSDKId)
      } catch (_e) {
        // no-op
      }
    },
    [meeting, isValidParticipantId],
  )

  const unmuteParticipant = useCallback(
    (videoSDKId?: string) => {
      if (!isValidParticipantId(videoSDKId)) return
      try {
        const isLocal = meeting?.localParticipant?.id === videoSDKId
        if (typeof meeting?.unmuteMic === 'function') isLocal ? meeting.unmuteMic() : meeting.unmuteMic(videoSDKId)
      } catch (_e) {
        // no-op
      }
    },
    [meeting, isValidParticipantId],
  )

  const stopAllHumans = useCallback(() => {
    const ids = [human1?.id, human2?.id].filter(Boolean) as string[]
    if (ids.length) emitStopTranscription(ids)
    // also try muting both to enforce “floor”
    muteParticipant(human1?.videoSDKId)
    muteParticipant(human2?.videoSDKId)
  }, [human1?.id, human2?.id, human1?.videoSDKId, human2?.videoSDKId, emitStopTranscription, muteParticipant])

  const stopTtsPlayback = useCallback(() => {
    if (!hall?.id || !clerkUser?.id) return
    try {
      socket.emit('crossfire-stop-tts', { hallId: hall.id, userId: clerkUser.id })
    } catch (_e) {
      // no-op
    }
    if (hallMetadata?.activeTts) setHallMetadata({ activeTts: '' })
  }, [clerkUser?.id, hall?.id, hallMetadata?.activeTts, setHallMetadata, socket])

  const getContextForStep = useCallback(() => {
    const step = turn.step
    const arr = (transcriptions as any)?.[step] ?? []
    return (Array.isArray(arr) ? arr : [])
      .slice(-12)
      .map((t: any) => ({
        speakerName: t?.hallUser?.displayName,
        speakingRole: t?.role,
        text: t?.text,
      }))
      .filter((t: any) => typeof t.text === 'string' && t.text.trim())
  }, [transcriptions, turn.step])

  const waitForTtsToFinish = useCallback(
    async (timeoutMs: number) => {
      const start = Date.now()
      const pollMs = 120
      // Wait for element existence + src
      while (Date.now() - start < timeoutMs) {
        const el = audioRef.current as HTMLAudioElement | null
        if (el && el.src) break
        await new Promise((r) => setTimeout(r, pollMs))
      }

      const el = audioRef.current as HTMLAudioElement | null
      if (!el || !el.src) return false

      // Wait until playback ends (or timeout). If autoplay is blocked, this will also time out.
      return await new Promise<boolean>((resolve) => {
        const remaining = timeoutMs - (Date.now() - start)
        const done = () => {
          try {
            el.removeEventListener('ended', done)
          } catch {}
          resolve(true)
        }
        try {
          el.addEventListener('ended', done, { once: true })
        } catch {
          resolve(false)
          return
        }
        setTimeout(() => {
          try {
            el.removeEventListener('ended', done)
          } catch {}
          resolve(false)
        }, Math.max(500, remaining))
      })
    },
    [audioRef],
  )

  const runAiUtterance = useCallback(
    async (mode: 'question' | 'answer', botUser: any, seat: 'slot1' | 'slot2') => {
      if (!hall?.id || !clerkUser?.id) return
      const context = getContextForStep()

      setBusy(true)
      try {
        setStatusMsg(mode === 'question' ? 'Fetching question…' : 'Fetching response…')
        const utter = await axios.post('/ai/crossfire-utterance', {
          resolution: hall.topic,
          mode,
          turnType: turn.type,
          seat,
          context,
        })

        const text: string = (utter?.data?.text || '').trim()
        if (!text) return

        setStatusMsg('Updating transcript…')
        updateTranscript(turn.step, {
          id: `transcript-${Date.now()}-${Math.random()}`,
          text,
          hallUser: botUser,
          turnStep: turn.step,
          role: botUser.speakingRole,
        } as any)

        // Flow notes: best-effort (will fall back to basic notes if generator fails)
        try {
          await fetchFlowNote({ transcript: text, step: turn.step, turnType: turn.type })
        } catch {}

        setStatusMsg('Fetching audio…')
        const ttsRes = await axios.post('/tts/generate-tts', {
          text,
          hallId: hall.id,
          botId: botUser.botId,
          userId: clerkUser.id,
        })

        const activeTts: string = ttsRes?.data?.activeTts
        if (activeTts) {
          setHallMetadata({ status: TurnStatus.PLAYING })
          setHallMetadata({ activeTts })
          socket.emit('crossfire-play-tts', { hallId: hall.id, userId: clerkUser.id, activeTts })
        }
        setStatusMsg(mode === 'question' ? 'Question ready.' : 'Response ready.')
      } finally {
        setBusy(false)
      }
    },
    [
      axios,
      clerkUser?.id,
      hall?.id,
      hall?.topic,
      isBot,
      socket,
      turn.step,
      turn.type,
      updateTranscript,
      setHallMetadata,
      getContextForStep,
      fetchFlowNote,
    ],
  )

  const startHuman = useCallback(
    (me: any, other: any, label: string) => {
      if (!me) return

      // enforce floor: stop both, then enable the chosen one
      stopAllHumans()

      // unmute chosen, mute other
      unmuteParticipant(me.videoSDKId)
      muteParticipant(other?.videoSDKId)

      // ensure timer runs like normal crossfire
      setHallMetadata({ status: TurnStatus.RECORDING })
      setStatusMsg(`Recording ${label}…`)
      emitStartTranscription([me.id])
    },
    [
      emitStartTranscription,
      muteParticipant,
      stopAllHumans,
      unmuteParticipant,
      setHallMetadata,
      setStatusMsg,
    ],
  )

  const onStartQuestioner = useCallback(async () => {
    if (!canRun) return
    stopAllHumans()
    if (!questioner || !responder) return

    if (questionerIsBot) {
      setPhase('playingQuestion')
      await runAiUtterance('question', questioner, 'slot1')
      // Wait for the question audio to finish before enabling response.
      setStatusMsg('Playing question…')
      const finished = await waitForTtsToFinish(35_000)
      if (!finished) {
        setStatusMsg('Question audio may be blocked. Click Play once, then continue.')
      } else {
        setStatusMsg('Ready for response.')
      }
      setPhase('readyAnswer')
      return
    }
    startHuman(questioner, responder, 'question')
    // For human questioner we require explicit stop to finalize transcript; use "Stop Answer" pattern doesn't apply.
    setPhase('answerRecording')
  }, [canRun, questioner, responder, questionerIsBot, runAiUtterance, startHuman, stopAllHumans, waitForTtsToFinish])

  const onStartAnswer = useCallback(async () => {
    if (!canRun) return
    stopAllHumans()
    if (!questioner || !responder) return

    if (responderIsBot) {
      setPhase('playingAnswer')
      await runAiUtterance('answer', responder, 'slot2')
      setStatusMsg('Playing response…')
      const finished = await waitForTtsToFinish(35_000)
      if (!finished) {
        setStatusMsg('Response audio may be blocked. Click Play once, then continue.')
      } else {
        setStatusMsg('Cross-ex complete. Ready to advance.')
      }
      setPhase('readyAdvance')
      return
    }
    startHuman(responder, questioner, 'response')
    setPhase('answerRecording')
  }, [canRun, stopAllHumans, questioner, responder, responderIsBot, runAiUtterance, startHuman, waitForTtsToFinish])

  const onStopAnswer = useCallback(async () => {
    if (!canRun) return
    if (!responder) return
    if (!responderIsHuman) return

    setStatusMsg('Processing answer…')
    emitStopTranscription([responder.id])
    muteParticipant(responder?.videoSDKId)

    // Give the live transcription a brief moment to flush its last buffered segment.
    await new Promise((r) => setTimeout(r, 400))
    void fetchFlowNote({ step: turn.step, turnType: turn.type })

    setStatusMsg('Cross-ex complete. Ready to advance.')
    setPhase('readyAdvance')
  }, [canRun, responder, responderIsHuman, emitStopTranscription, muteParticipant, fetchFlowNote, turn.step, turn.type])

  const advanceToNextTurn = useCallback(() => {
    // mirror UserTurn's onNext behavior (simplified but consistent)
    const activeIdx = hallMetadata?.activeTurn ?? -1
    const turns = hallMetadata?.turns ?? []
    const nextTurn = turns?.[activeIdx + 1]

    const shouldPinSecondary =
      nextTurn?.type === TurnType.AffirmativeCrossNegative ||
      nextTurn?.type === TurnType.NegativeCrossAffirmative

    const speakers =
      !nextTurn || nextTurn?.type === TurnType.Preperatory
        ? []
        : shouldPinSecondary
          ? [nextTurn?.primarySpeakerId, nextTurn?.secondarySpeakerId].filter(Boolean)
          : [nextTurn?.primarySpeakerId].filter(Boolean)

    const newMetadata: any = {
      activeTurn: (hallMetadata?.activeTurn ?? -1) + 1,
      activeTts: '',
      timer: nextTurn ? toMs(nextTurn?.duration) : 0,
      debateComplete: !nextTurn ? true : false,
      pinnedParticipants: speakers,
      status: TurnStatus.READY,
    }

    if (!nextTurn) {
      if (hall?.winType === WinType.AI) {
        setSideBarMode((s: any) => (s === sideBarModes.JUDGMENT ? null : sideBarModes.JUDGMENT))
        setHallMetadata({
          debateComplete: true,
          pollingStarted: true,
          ...newMetadata,
        })
      } else if ([WinType.SIMPLEPOLL, WinType.EVALUATIONPOLL, WinType.BALLOT].includes(hall?.winType)) {
        setHallMetadata({ pollingStarted: true, ...newMetadata })
      }
      if (hall?.winType !== WinType.AI) setSideBarMode(null)
    } else {
      setHallMetadata(newMetadata)
    }

    setPhase('readyQuestion')
    setStatusMsg('')
  }, [hallMetadata?.activeTurn, hallMetadata?.turns, hall?.winType, setHallMetadata, setSideBarMode])

  if (!canRun) return null

  const label = (() => {
    switch (phase) {
      case 'readyQuestion':
        return 'Ready: Question'
      case 'playingQuestion':
        return 'Playing: Question'
      case 'readyAnswer':
        return 'Ready: Answer'
      case 'answerRecording':
        return 'Recording: Answer'
      case 'playingAnswer':
        return 'Playing: Answer'
      case 'readyAdvance':
        return 'Ready: Advance'
      default:
        return ''
    }
  })()

  return (
    <div className="mt-2 border rounded p-2">
      <div className="text-xs text-muted-foreground mb-2">Crossfire Control (moderator)</div>
      <div className="text-xs mb-2">{label}</div>

      {statusMsg ? (
        <div className="text-xs mb-2 w-full rounded p-1 text-center border">
          {statusMsg}
        </div>
      ) : null}

      <div className="flex items-center justify-between w-full">
        {/* Left: primary action button that changes state */}
        <div className="flex items-center gap-2 flex-wrap">
          {phase === 'readyQuestion' && (
            <Button variant="outline" className="text-xs" onClick={onStartQuestioner} disabled={busy}>
              Start Question
            </Button>
          )}

          {phase === 'playingQuestion' && (
            <Button variant="outline" className="text-xs" disabled>
              Playing Question…
            </Button>
          )}

          {phase === 'readyAnswer' && (
            <Button variant="outline" className="text-xs" onClick={onStartAnswer} disabled={busy}>
              Start Answer
            </Button>
          )}

          {phase === 'playingAnswer' && (
            <Button variant="outline" className="text-xs" disabled>
              Playing Answer…
            </Button>
          )}

          {phase === 'answerRecording' && (
            <Button variant="outline" className="text-xs" onClick={onStopAnswer} disabled={busy}>
              Stop Answer
            </Button>
          )}
        </div>

        {/* Right: always-available Next (matches other controllers) */}
        <div className="flex items-center space-x-3">
          <NextControl
            onClick={() => {
              if (busy) return
              // Always allow skipping to the next debate turn.
              // Ensure we stop any running processes first.
              try {
                stopAllHumans()
              } catch {}
              try {
                stopTtsPlayback()
              } catch {}
              void (phase === 'answerRecording' ? onStopAnswer().finally(advanceToNextTurn) : Promise.resolve(advanceToNextTurn()))
            }}
          />
        </div>
      </div>
    </div>
  )
}


